/**
 * Focus fit: frame the recent path (the last few words, current included)
 * plus the target, instead of the whole start..target span, so the words the
 * player is choosing between get room. The start stays in frame only while
 * that costs little zoom; otherwise an edge indicator points to it.
 */
import { fitCamera, project, type Camera, type Size } from "./layout";

/** Path words kept in frame: the current word and the 4 before it. */
export const FOCUS_RECENT = 5;
/** Keep the start in frame if that keeps >= 60% of the focus-only zoom. */
export const FOCUS_ZOOM_BUDGET = 0.6;
/** Zoom cap: the frame never spans less than 8% of the graph's extent. */
export const FOCUS_MIN_SPAN_FRACTION = 0.08;
/** Indicator inset from the board edge, px. */
export const INDICATOR_INSET = 16;

type Pt = { x: number; y: number };

export interface FocusFit {
  camera: Camera;
  startInFrame: boolean;
}

export function focusCamera(args: {
  recent: Pt[];
  target: Pt;
  start: Pt | null;
  size: Size;
  pad: number;
  minSpan: number;
  budget?: number;
  /** "Tight": the target, like the start, stays in frame only within budget. */
  tight?: boolean;
}): FocusFit {
  const { recent, target, start, size, pad, minSpan } = args;
  const budget = args.budget ?? FOCUS_ZOOM_BUDGET;
  const opts = { pad, minSpan };
  if (args.tight) {
    const base = fitCamera(recent, size, opts);
    const keep = (extra: Pt[]) => {
      const c = fitCamera([...recent, ...extra], size, opts);
      return c.s >= budget * base.s ? c : null;
    };
    const withTarget = keep([target]);
    const extra = withTarget ? [target] : [];
    const withStart = start ? keep([...extra, start]) : null;
    return {
      camera: withStart ?? withTarget ?? base,
      startInFrame: !!withStart,
    };
  }
  const focus = fitCamera([...recent, target], size, opts);
  if (!start) return { camera: focus, startInFrame: false };
  const withStart = fitCamera([...recent, target, start], size, opts);
  return withStart.s >= budget * focus.s
    ? { camera: withStart, startInFrame: true }
    : { camera: focus, startInFrame: false };
}

export const onBoard = (
  p: { px: number; py: number },
  size: Size,
  margin = 0,
) =>
  p.px >= margin &&
  p.py >= margin &&
  p.px <= size.w - margin &&
  p.py <= size.h - margin;

export interface EdgeIndicator {
  id: string;
  /** Where the arrow sits, inset from the board edge. */
  x: number;
  y: number;
  /** Radians; the arrow points this way (toward the off-screen word). */
  angle: number;
}

/**
 * Where to draw an indicator for a word projected off the board: on the
 * ray from the board centre toward it, clamped to the inset rectangle.
 */
export function edgeIndicator(
  id: string,
  p: { px: number; py: number },
  size: Size,
  inset = INDICATOR_INSET,
): EdgeIndicator {
  const cx = size.w / 2;
  const cy = size.h / 2;
  const dx = p.px - cx;
  const dy = p.py - cy;
  const hw = Math.max(1, size.w / 2 - inset);
  const hh = Math.max(1, size.h / 2 - inset);
  const t = Math.min(
    dx === 0 ? Infinity : hw / Math.abs(dx),
    dy === 0 ? Infinity : hh / Math.abs(dy),
  );
  return { id, x: cx + dx * t, y: cy + dy * t, angle: Math.atan2(dy, dx) };
}

export { project };
